/**
 * TutorProfileView — full read-only profile panel.
 * Renders bio, experience, education, specialization, languages, certifications,
 * subjects+rates, and weekly availabilities from a GET /tutors/{id} response.
 *
 * Props:
 *   tutor     {Object|null}  full tutor resource (with user, subjects, availabilities)
 *   loading   {boolean}
 */

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 font-montserrat mb-2">
      {title}
    </h3>
    {children}
  </div>
);

const Value = ({ children }) => (
  <p className="text-sm font-montserrat text-[#4A4A4A]">{children || '—'}</p>
);

const TutorProfileView = ({ tutor, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="text-gray-400 font-montserrat text-sm">Chargement du profil…</span>
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="text-gray-400 font-montserrat text-sm">
          Sélectionnez un répétiteur pour voir son profil.
        </span>
      </div>
    );
  }

  const user = tutor.user ?? {};
  const subjects = tutor.subjects ?? [];
  const availabilities = tutor.availabilities ?? [];

  // Sort availabilities by canonical day order
  const sortedAvailabilities = [...availabilities].sort(
    (a, b) =>
      DAY_ORDER.indexOf(a.day_of_week) - DAY_ORDER.indexOf(b.day_of_week)
  );

  const languages = Array.isArray(tutor.languages) ? tutor.languages : [];
  const certifications = Array.isArray(tutor.certifications) ? tutor.certifications : [];

  return (
    <div className="space-y-1">
      {/* Identity */}
      <div className="mb-5">
        <h2 className="text-lg font-playfair font-bold text-[#17386E] mb-0.5">
          {user.first_name ?? ''} {user.last_name ?? ''}
        </h2>
        <p className="text-sm text-gray-500 font-montserrat">{user.email ?? '—'}</p>
      </div>

      <Section title="Biographie">
        <Value>{tutor.bio}</Value>
      </Section>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 font-montserrat mb-1">
            Expérience
          </h3>
          <Value>
            {tutor.experience_years != null
              ? `${tutor.experience_years} an${tutor.experience_years > 1 ? 's' : ''}`
              : null}
          </Value>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 font-montserrat mb-1">
            Niveau d'études
          </h3>
          <Value>{tutor.education_level}</Value>
        </div>
      </div>

      <Section title="Spécialisation">
        <Value>{tutor.specialization}</Value>
      </Section>

      <Section title="Langues">
        {languages.length ? (
          <div className="flex flex-wrap gap-1">
            {languages.map((lang, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EAF4FB] text-[#17386E]"
              >
                {lang}
              </span>
            ))}
          </div>
        ) : (
          <Value>{null}</Value>
        )}
      </Section>

      <Section title="Certifications">
        {certifications.length ? (
          <ul className="list-disc list-inside space-y-0.5">
            {certifications.map((cert, i) => (
              <li key={i} className="text-sm font-montserrat text-[#4A4A4A]">
                {cert}
              </li>
            ))}
          </ul>
        ) : (
          <Value>{null}</Value>
        )}
      </Section>

      <Section title="Matières et tarifs">
        {subjects.length ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs font-montserrat">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-left">
                  <th className="px-3 py-2">Matière</th>
                  <th className="px-3 py-2">Niveaux</th>
                  <th className="px-3 py-2 text-right">Tarif/h</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subjects.map((s) => {
                  const levels = Array.isArray(s.pivot?.academic_levels)
                    ? s.pivot.academic_levels.join(', ')
                    : '—';
                  const rate =
                    s.pivot?.hourly_rate != null
                      ? `${s.pivot.hourly_rate} €`
                      : '—';
                  return (
                    <tr key={s.id}>
                      <td className="px-3 py-2 text-[#4A4A4A]">{s.name_fr ?? s.name_en ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{levels}</td>
                      <td className="px-3 py-2 text-right font-medium text-[#17386E]">{rate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Value>{null}</Value>
        )}
      </Section>

      <Section title="Disponibilités hebdomadaires">
        {sortedAvailabilities.length ? (
          <div className="space-y-1">
            {sortedAvailabilities.map((a) => (
              <div
                key={a.id}
                className="flex justify-between text-xs font-montserrat text-[#4A4A4A] bg-gray-50 rounded-lg px-3 py-1.5"
              >
                <span className="font-medium capitalize">
                  {a.day_of_week_label ?? a.day_of_week}
                </span>
                <span className="text-gray-500">
                  {a.start_time} – {a.end_time}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Value>{null}</Value>
        )}
      </Section>

      {tutor.rejection_reason && (
        <Section title="Motif de rejet précédent">
          <p className="text-sm font-montserrat text-red-700 bg-red-50 rounded-lg px-3 py-2">
            {tutor.rejection_reason}
          </p>
        </Section>
      )}
    </div>
  );
};

export default TutorProfileView;
